import useAbortController from '@hooks/useAbortController';
import trackPlaceOrderClick from '@helpers/analyticsHelpers/trackPlaceOrderClick';
import { CheckoutError, placeOrder } from '@api/interfaces/checkoutApi';
import { PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import { PlaceOrderErrorResponse } from '@enums/PlaceOrderErrorResponse';
import { setEmptyCartModalVisibility } from '@slices/modalSlice';
import paths from '@constants/paths';
import { setCompleted, setEditing, setErrorMessage, setSelectedMode } from '@slices/checkoutSlice';
import logger from '@logger';
import { Dispatch, SetStateAction } from 'react';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import useStore from '@hooks/useStore';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useRouter } from 'next/navigation';

interface Props {
  possiblyWrongStoreConfirmed: PossiblyWrongStoreConfirmedState;
  setError: (error: ErrorDataType) => void;
  setPaymentInProgress: Dispatch<SetStateAction<boolean>>;
  openBankIdModal: () => void;
}

const usePlaceOrder = ({ possiblyWrongStoreConfirmed, setError, setPaymentInProgress, openBankIdModal }: Props) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const checkCartConflict = useCheckCartConflict({ checkStock: true });
  const { store } = useStore();
  const { getSignal } = useAbortController();

  const placeOrderHandler = async (orderRef = '') => {
    trackPlaceOrderClick();
    setPaymentInProgress(true);
    try {
      await checkCartConflict({ storeId: store?.storeId });

      // @ts-ignore
      window.location.href = await placeOrder(
        {
          orderRef,
          selectedCardId: 'NONE',
          userClickedContinue:
            possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL ||
            possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY,
        },
        getSignal()
      );
    } catch (e) {
      if (e instanceof CheckoutError) {
        if (e.status === '401') {
          // Auth renewal handled by global fetch interceptor
          setPaymentInProgress(false);
          return;
        }
        if (e.details === PlaceOrderErrorResponse.BANK_ID_PROMPT) {
          openBankIdModal();
        } else {
          if (e.details === PlaceOrderErrorResponse.EMPTY_CART) {
            dispatch(setEmptyCartModalVisibility(true));
            router.push(paths.HOME);
          }
          if (e.details === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
            dispatch(setCompleted({ boxName: 'delivery', isCompleted: false }));
            dispatch(setEditing({ boxName: 'delivery', isEditing: true }));
            dispatch(setErrorMessage(e.details));
            setError({ errorCode: e.details });
          } else {
            setError({ errorCode: e.details || 'default' });
            dispatch(setSelectedMode(''));
            logger.error({ error: 'invoice error' });
          }
          setPaymentInProgress(false);
        }
      } else {
        setError({ errorCode: 'default' });
        setPaymentInProgress(false);
      }
    }
  };

  return {
    placeOrderHandler,
  };
};

export default usePlaceOrder;
